import { getShareKeyframes, getShareMeetingSmartChapters } from "@/services/share";
import { getKeyframes, getMeetingSmartChapters } from "@/services/summary";
import { IChapterApi, IKeyframeItem } from "@/types/summary";
import { create } from "zustand";
import summaryCommonStore from "./summaryCommon.store";
import { AxiosRequestConfig } from "axios";
import { MeetingRecordVisibility, Origin } from "@/constants/share";

interface SummaryChapterState {
  chapterInfo: IChapterApi | null;
  lastChapterInfo: IChapterApi | null;
  newChapterInfo: Partial<IChapterApi> | null;
  chapterLoading: boolean;
  keyframeArr: IKeyframeItem[];
}

interface SummaryChapterStore extends SummaryChapterState {
  fetchChapterInfo: (param: { streamName: string; reload: boolean; contentStep: string; cover: boolean }, needThrowError?: boolean, config?: AxiosRequestConfig<unknown>) => Promise<IChapterApi | undefined>;
  modifyChapterInfo: (newData: Partial<IChapterApi>) => Promise<void>;
  initNewChapterInfo: () => void;
  setChapterLoading: (loading: boolean) => void;
  setLastChapterInfo: (chapterInfo: IChapterApi | null) => void;
  resetLastChapterInfo: () => void;
  initStore: () => void;
  fetchKeyframe: (streamName: string) => void;
}

const summaryChapterState: SummaryChapterState = {
  chapterLoading: false,
  chapterInfo: null,
  lastChapterInfo: null,
  newChapterInfo: null,
  keyframeArr: []
};

const summaryChapterStore = create<SummaryChapterStore>()((set, get) => ({
  ...summaryChapterState,
  initStore: () => {
    set({ ...summaryChapterState });
  },
  fetchChapterInfo: async (params, needThrowError = false, config) => {
    try {
      let res;
      const { streamName } = params;
      const shareInfo = summaryCommonStore.getState().shareInfo;
      const useComplete = shareInfo?.origin === Origin.MEETING_PLATE && shareInfo?.fileTransEnable;
      const formatParams = {...params, contentStep: useComplete ? "COMPLETE_DOC" : "SRC_DOC"}
      if (shareInfo && summaryCommonStore.getState().shareScope !== MeetingRecordVisibility.orgMember) {
        res = await getShareMeetingSmartChapters({ ...formatParams, ...shareInfo }, config);
      } else {
        res = await getMeetingSmartChapters(formatParams, config);
      }
  
      if (!res) {
        throw new Error("获取章节失败");
      }
  
      const streamId = summaryCommonStore.getState().streamId;
      if (!streamName.includes(streamId)) {
        return;
      }
  
      const targetContent = useComplete ? 'complateChapterList' : 'srcChapterList';
      set({ chapterInfo: {...res, srcChapterList: res[targetContent]} });
      return res;
    } catch (error) {
      console.log('fetchChapterInfo error', error);
      if (needThrowError) {
        throw error;
      }
    }
  },
  fetchKeyframe: async (streamName: string) => {
    try {
      let res;
      const shareInfo = summaryCommonStore.getState().shareInfo;
      if (shareInfo) {
        res = await getShareKeyframes(streamName);
      } else {
        res = await getKeyframes(streamName);
      }

      if (!res) {
        throw new Error("获取关键帧失败");
      }
      set({
        keyframeArr: res.data.sort((a, b) => a.beginTimestamp - b.beginTimestamp)
      });
    } catch (error) {
      console.log('fetchKeyframe error', error);
    }
  },
  modifyChapterInfo: async (newData) => {
    try {
      set({ newChapterInfo: newData });
    } catch (error) {
      console.error(error);
    }
  },
  initNewChapterInfo: () => {
    const { modifyChapterInfo, chapterInfo } = get();
    if (!chapterInfo) {
      return;
    }
    modifyChapterInfo(chapterInfo);
  },
  setChapterLoading: (loading) => {
    set({ chapterLoading: loading });
  },
  setLastChapterInfo: (chapterInfo) => {
    set({ lastChapterInfo: chapterInfo });
  },
  resetLastChapterInfo: () => {
    set({ chapterInfo: get().lastChapterInfo, lastChapterInfo: null });
  },
}));

export default summaryChapterStore;
